import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Link2Off, ExternalLink } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Footer } from '../components/Footer';
import { API } from '@/lib/helpers';
import { trackView, trackClick } from '@/lib/analytics';

const FONT_MAP = {
  'Inter': "'Inter', sans-serif",
  'Georgia': "'Georgia', serif",
  'Playfair Display': "'Playfair Display', serif",
  'Roboto Mono': "'Roboto Mono', monospace",
  'Poppins': "'Poppins', sans-serif",
};

const BG_PRESETS = {
  lavender_mist: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 50%, #f0e7fe 100%)',
  rose_quartz: 'linear-gradient(135deg, #fff1f2 0%, #fce7f3 50%, #fdf2f8 100%)',
  morning_sky: 'linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)',
  sage_garden: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f0fdf9 100%)',
  warm_sand: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 40%, #fff7ed 100%)',
  pearl: 'linear-gradient(180deg, #fafafa 0%, #f5f5f4 50%, #fafaf9 100%)',
  soft_lilac: 'linear-gradient(180deg, #faf5ff 0%, #f3e8ff 60%, #faf5ff 100%)',
  ocean_breeze: 'linear-gradient(135deg, #ecfeff 0%, #e0f2fe 50%, #eff6ff 100%)',
  blush_peach: 'linear-gradient(135deg, #fff7ed 0%, #fff1f2 50%, #fef2f2 100%)',
  mint_cream: 'linear-gradient(180deg, #f0fdfa 0%, #ecfdf5 50%, #f0fdf4 100%)',
  twilight: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #3730a3 100%)',
  midnight: 'linear-gradient(180deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
};

function getBackgroundStyle(page) {
  const bgType = page.bg_type || 'gradient';
  if (bgType === 'gradient') {
    const dir = page.bg_gradient_direction || 'to bottom right';
    const c1 = page.cor_fundo || '#f8fafc';
    const c2 = page.cor_fundo_2 || '#e5f6ff';
    return { background: `linear-gradient(${dir}, ${c1}, ${c2})` };
  }
  if (bgType === 'preset' && page.bg_preset && BG_PRESETS[page.bg_preset]) {
    return { background: BG_PRESETS[page.bg_preset] };
  }
  return { backgroundColor: page.cor_fundo || '#f8fafc' };
}

function normalizeUrl(url) {
  if (!url) return '#';
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^mailto:|^tel:|^#|^\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function getIcon(name, props = {}) {
  const Icon = LucideIcons[name];
  return Icon ? <Icon {...props} /> : <LucideIcons.Link {...props} />;
}

export default function PublicLinkPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!slug) { setError(true); setLoading(false); return; }
    fetch(`${API}/public/linkpage/${slug}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setData(d); document.title = 'CCBRJM'; trackView('linktree', slug, ''); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="text-center" data-testid="linkpage-error">
        <div className="w-16 h-16 bg-slate-100 rounded-full mx-auto mb-4 flex items-center justify-center">
          <Link2Off className="w-8 h-8 text-slate-400" />
        </div>
        <h2 className="text-lg font-semibold text-slate-700 mb-1">Página não encontrada</h2>
        <p className="text-sm text-slate-500">O link que você acessou não existe ou foi desativado.</p>
      </div>
    </div>
  );

  const { page, links } = data;

  if (!links || links.length === 0) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="text-center" data-testid="linkpage-error">
        <div className="w-16 h-16 bg-slate-100 rounded-full mx-auto mb-4 flex items-center justify-center">
          <Link2Off className="w-8 h-8 text-slate-400" />
        </div>
        <h2 className="text-lg font-semibold text-slate-700 mb-1">Página não encontrada</h2>
        <p className="text-sm text-slate-500">O link que você acessou não existe ou foi desativado.</p>
      </div>
    </div>
  );

  const fontFamily = FONT_MAP[page.fonte] || FONT_MAP['Inter'];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Georgia&family=Playfair+Display:wght@400;600;700&family=Roboto+Mono:wght@400;500&family=Poppins:wght@400;500;600;700&display=swap');
      `}</style>
      <div
        className="min-h-screen flex flex-col"
        style={{ ...getBackgroundStyle(page), fontFamily }}
        data-testid="linkpage-container"
      >
        <div className="flex-1 flex flex-col items-center px-4 py-10">
          <div className="w-full max-w-md">
          {/* Page Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 rounded-2xl backdrop-blur shadow-lg flex items-center justify-center"
              style={{ backgroundColor: page.cor_fundo_icone || '#2663eb' }}>
              {getIcon(page.icone_pagina || 'Home', { className: 'w-10 h-10', style: { color: page.cor_icone || '#ffffff' } })}
            </div>
          </div>

          {/* Church Name */}
          {page.nome_igreja && (
            <h1
              className="text-2xl font-bold text-center mb-1"
              style={{ color: page.cor_fonte_nome }}
              data-testid="linkpage-title"
            >
              {page.nome_igreja}
            </h1>
          )}

          {/* Bio */}
          {page.bio && (
            <p
              className="text-sm text-center mb-8 max-w-xs mx-auto leading-relaxed"
              style={{ color: page.cor_fonte_bio }}
              data-testid="linkpage-bio"
            >
              {page.bio}
            </p>
          )}

          {/* Links */}
          <div className="space-y-4" data-testid="linkpage-links">
            {links.map((link) => (
              <a
                key={link.link_id}
                href={normalizeUrl(link.url)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackClick('linktree', slug, '', link.link_id, { titulo: link.titulo })}
                className="block w-full transition-all duration-200 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
                style={{
                  backgroundColor: link.cor_botao,
                  borderColor: link.cor_borda,
                  borderWidth: `${link.tamanho_borda}px`,
                  borderStyle: link.tipo_borda,
                  borderRadius: `${link.border_radius}px`,
                  padding: '14px 20px',
                  marginTop: '12px',
                }}
                data-testid={`linkpage-link-${link.link_id}`}
              >
                <div className="flex items-center gap-3">
                  {getIcon(link.icone, {
                    className: 'w-5 h-5 flex-shrink-0',
                    style: { color: link.cor_fonte },
                  })}
                  <div className="flex-1 min-w-0">
                    <p
                      className="font-semibold text-sm truncate"
                      style={{ color: link.cor_fonte }}
                    >
                      {link.titulo}
                    </p>
                    {link.descricao && (
                      <p
                        className="text-xs truncate mt-0.5 opacity-80"
                        style={{ color: link.cor_fonte }}
                      >
                        {link.descricao}
                      </p>
                    )}
                  </div>
                  <ExternalLink
                    className="w-4 h-4 flex-shrink-0 opacity-50"
                    style={{ color: link.cor_fonte }}
                  />
                </div>
              </a>
            ))}
          </div>

          {links.length === 0 && (
            <p className="text-center text-sm mt-8" style={{ color: page.cor_fonte_bio }}>
              Nenhum link disponível no momento.
            </p>
          )}
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}
